import { useEffect, useRef, useState } from "react";
import { getFreshness, seasonPrompts, type Freshness } from "../lib/freshness";

interface PromptsSheetProps {
  open: boolean;
  onClose: () => void;
  onSelect: (prompt: string) => void;
}

interface PromptGroup {
  icon: string;
  label: string;
  prompts: string[];
}

/**
 * The "What can I ask?" sheet, pared on 2026-11-04 (docs/answer-coverage-plan.md phase 0b).
 * Rule: one prompt per answer family, no two prompts that route to the same
 * intent in the same shape, nothing the app cannot answer today, and no
 * overlap with the six starters in ChatThread. To add a prompt, first ask
 * which family it belongs to; if the family is already here, replace rather
 * than append. The GBL Season group is generated from the current season.
 */
export const PROMPT_GROUPS: PromptGroup[] = [
  {
    icon: "📊",
    label: "Stats & lore",
    prompts: ["What type is Togekiss?", "Tell me about Gengar", "Can Mewtwo be shiny?"],
  },
  {
    icon: "🏆",
    label: "PvP",
    prompts: [
      "How good is Medicham in Great League?",
      "Who are the top Pokémon in Ultra League?",
      "What's the best moveset for Swampert in Great League?",
      "What beats Registeel in Great League?",
      "Does Gengar beat Mamoswine in Ultra League?",
      "Swampert vs Feraligatr in Great League",
    ],
  },
  {
    icon: "🆕",
    label: "GBL Season",
    // Built from the current season at render time (seasonPrompts) — a fixed
    // list went stale at every season change ("Tell me about Forever Forward").
    prompts: [],
  },
  {
    icon: "🟢",
    label: "Right now",
    prompts: [
      "What cups are running today?",
      "What's in raids today?",
      "When's the next Community Day?",
      "What's hatching from eggs right now?",
      "What field research is available today?",
      "How do I beat Arlo?",
    ],
  },
  {
    icon: "🔢",
    label: "IVs & levels",
    prompts: [
      "What rank is my 0/14/15 Azumarill in Great League?",
      "What are the best IVs for Swampert in Great League?",
      "How much Stardust from level 20 to 40?",
    ],
  },
  {
    icon: "⚡",
    label: "Moves & evolution",
    prompts: [
      "What moves can Charizard learn?",
      "Is Mud Shot a good fast move?",
      "Compare Vine Whip vs Razor Leaf",
      "What Elite TM moves does Charizard have?",
      "How much candy does it cost to evolve Dratini?",
      "How much Mega Energy does Charizard need?",
    ],
  },
  {
    icon: "🥊",
    label: "Raids",
    prompts: ["Who counters Tyranitar in raids?", "Who are the best Fire attackers?", "What does rainy weather boost?"],
  },
  {
    icon: "📖",
    label: "Game terms",
    prompts: ["What is a lucky trade?"],
  },
];

export default function PromptsSheet({ open, onClose, onSelect }: PromptsSheetProps) {
  const [freshness, setFreshness] = useState<Freshness | null>(null);
  useEffect(() => {
    let alive = true;
    void getFreshness().then((f) => {
      if (alive) setFreshness(f);
    });
    return () => {
      alive = false;
    };
  }, []);
  const groups = PROMPT_GROUPS.map((g) => (g.label === "GBL Season" ? { ...g, prompts: seasonPrompts(freshness) } : g));
  const sheetRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Trap scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Supported prompts"
        className={`fixed bottom-0 left-0 right-0 z-50 bg-scout-bg border-t border-white/[0.08] rounded-t-2xl shadow-2xl
          flex flex-col max-h-[75vh] transition-transform duration-300 ease-out
          ${open ? "translate-y-0" : "translate-y-full"}`}
      >
        {/* Handle + header */}
        <div className="flex-shrink-0 px-4 pt-3 pb-2">
          {/* Drag handle */}
          <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-3" />

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white">What can I ask?</h2>
              <p className="text-[11px] text-white/35 mt-0.5">Tap any prompt to use it</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-11 h-11 rounded-full bg-white/[0.06] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-5">
          {groups.map((group) => (
            <div key={group.label}>
              {/* Category header */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base leading-none" aria-hidden="true">{group.icon}</span>
                <span className="text-[11px] font-semibold text-white/40 uppercase tracking-widest">{group.label}</span>
              </div>

              {/* Prompt chips */}
              <div className="flex flex-col gap-1.5">
                {group.prompts.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => { onSelect(prompt); onClose(); }}
                    className="w-full text-left glass rounded-xl px-3.5 py-2.5 text-sm text-white/70
                      hover:text-white hover:bg-white/[0.07] transition-all group flex items-center gap-2"
                  >
                    <span className="text-scout-yellow opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0 text-xs">→</span>
                    <span>{prompt}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
